import {useEffect,useMemo,useRef,useState} from 'react';
import {ICON_OPTIONS} from '../utils/buttonIcons';
import './IconPicker.css';

function IconPicker({value,onChange}) {
  const [isOpen,setIsOpen]=useState(false);
  const [query,setQuery]=useState('');
  const pickerRef=useRef(null);
  const triggerRef=useRef(null);
  const selectedIcon=ICON_OPTIONS.find((icon)=> icon.value===value);
  const filteredIcons=useMemo(
    ()=> ICON_OPTIONS.filter((icon)=> icon.label.toLowerCase().includes(query.toLowerCase())),
    [query]
  );

  useEffect(()=> {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown=(event)=> {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown=(event)=> {
      if (event.key==='Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown',handlePointerDown);
    document.addEventListener('keydown',handleKeyDown);
    return ()=> {
      document.removeEventListener('pointerdown',handlePointerDown);
      document.removeEventListener('keydown',handleKeyDown);
    };
  },[isOpen]);

  const chooseIcon=(iconValue)=> {
    onChange(iconValue);
    setIsOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  return (
    <div className="icon-picker" ref={pickerRef}>
      <button
        className={`icon-picker-trigger${isOpen ? ' is-open' : ''}`}
        type="button"
        ref={triggerRef}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={()=> {
          setIsOpen((open)=> !open);
          setQuery('');
        }}
      >
        <span className="icon-picker-trigger-preview">
          {selectedIcon ? <i className={`fas ${selectedIcon.value}`} aria-hidden="true" /> : <span aria-hidden="true">—</span>}
        </span>
        <span className="icon-picker-trigger-copy">
          <span className="icon-picker-trigger-label">Selected icon</span>
          <strong>{selectedIcon?.label || 'No icon'}</strong>
        </span>
        <span className={`icon-picker-chevron${isOpen ? ' rotated' : ''}`} aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div className="icon-picker-popover" role="dialog" aria-label="Choose a button icon">
          <div className="icon-picker-popover-heading">
            <strong>Choose an icon</strong>
            <span>{filteredIcons.length} available</span>
          </div>
          <input
            className="icon-picker-search"
            type="search"
            value={query}
            placeholder="Search icons"
            aria-label="Search icons"
            onChange={(event)=> setQuery(event.target.value)}
          />
          <div className="icon-picker-options">
            {filteredIcons.map((icon)=> (
              <button
                className={`icon-picker-option${value===icon.value ? ' selected' : ''}`}
                key={icon.value}
                type="button"
                aria-label={icon.label}
                aria-pressed={value===icon.value}
                title={icon.label}
                onClick={()=> chooseIcon(icon.value)}
              >
                <i className={`fas ${icon.value}`} aria-hidden="true" />
                <span>{icon.label}</span>
              </button>
            ))}
            <button
              className={`icon-picker-option icon-picker-none${value==='none' ? ' selected' : ''}`}
              type="button"
              aria-label="No icon"
              aria-pressed={value==='none'}
              onClick={()=> chooseIcon('none')}
            >
              <span className="icon-picker-empty" aria-hidden="true">—</span>
              <span>No icon</span>
            </button>
            {filteredIcons.length===0 && <p className="icon-picker-no-results">No matching icons.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default IconPicker;